import React from 'react';
import { Box, Typography } from '@mui/material';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { ToolbarButton } from './ToolbarButton';

export interface CopyFieldProps {
  /** What is copied, shown in full: a link, an id. */
  value: string;
  /** Names it for screen readers: "Link to this dashboard". */
  ariaLabel: string;
  /** The button's word. "Copy link" by default. */
  copyLabel?: string;
}

/**
 * Something to hand on, shown whole with a button that copies it: a share
 * link. The value wraps rather than being cut, so what will be pasted can be
 * read; the button says "Copied" for a moment after.
 *
 * For code, use `CodeBlock`, which has its own copy button.
 */
export function CopyField({ value, ariaLabel, copyLabel = 'Copy link' }: CopyFieldProps) {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard refused (an old browser, no permission): the text is on
      // screen to select by hand.
    }
  };

  return (
    <Box
      sx={(t) => ({
        display: 'flex', alignItems: 'center', gap: 1,
        pl: 1.5, pr: 0.5, py: 0.5,
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.default}`,
        bgcolor: t.tokens.surface.sunken,
      })}
    >
      <Typography
        variant="body2"
        aria-label={ariaLabel}
        sx={(t) => ({ flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: t.tokens.text.secondary,
          fontFamily: t.typography.fontFamilyMono })}
      >
        {value}
      </Typography>
      <ToolbarButton
        icon={copied ? <CheckRounded fontSize="small" /> : <ContentCopyRounded fontSize="small" />}
        onClick={() => { void copy(); }}
        aria-label={copied ? 'Copied' : copyLabel}
      >
        {copied ? 'Copied' : copyLabel}
      </ToolbarButton>
    </Box>
  );
}
